import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ProfileKeyFacts } from "../../components/profile/ProfileKeyFacts";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { ProfileTimeline } from "../../components/profile/ProfileTimeline";
import { ConfirmDialog, LoadingOverlay } from "../../components/ui";
import { adminApi, profilesApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { ACCESS_GRANT_COPY } from "../../lib/confirmDialog";
import { formatDate, SEGMENT_LABELS } from "../../lib/format";
import { IDENTITY_KYC_SLOT_LABELS, kycStatusLabel } from "../../lib/kyc";
import { axioTokens } from "../../theme";

const AFFILIATE_PROFILE_COPY = {
  back: "Affiliates",
  role: "Affiliate",
  city: "City",
  registered: "Registered",
  kycSubmitted: "KYC submitted",
  segments: "Segments",
  identityAccess: "Identity & access",
  affiliateDetails: "Affiliate details",
  kycDocuments: "KYC documents",
  kycDocumentsHint: "Private uploads available only to administrators.",
  noDocuments: "No documents uploaded yet.",
  privateKyc: "Private KYC profile — images are admin-only.",
  reactivateCta: "Reactivate affiliate",
  deactivateCta: "Deactivate affiliate",
  deactivateTitle: "Deactivate this affiliate?",
  reactivateTitle: "Reactivate this affiliate?",
  deactivateBody: "This immediately blocks sign-in. Historical referrals and payouts are retained.",
  reactivateBody: "Sign-in access will be restored.",
  reasonLabel: "Reason",
  confirmPrefix: "Type",
  confirmSuffix: "to confirm",
  accountUpdateFailed: "Could not change account access",
} as const;

function KycSlotImage({ affiliateId, slot }: { affiliateId: string; slot: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    adminApi
      .fetchAffiliateKycFile(affiliateId, slot)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load image");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [affiliateId, slot]);

  if (error) {
    return (
      <Text size="sm" c="red">
        {error}
      </Text>
    );
  }
  if (!url) {
    return <LoadingOverlay height="var(--axio-unit-180)" />;
  }
  return (
    <img
      src={url}
      alt={IDENTITY_KYC_SLOT_LABELS[slot] ?? slot}
      style={{ width: "var(--axio-percentage-full)", maxHeight: "var(--axio-unit-280)", objectFit: "contain", background: axioTokens.mediaBackground }}
    />
  );
}

export function AdminAffiliateDetailPage() {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [accountAction, setAccountAction] = useState<"deactivate" | "reactivate" | null>(null);
  const [accountReason, setAccountReason] = useState("");
  const [accountConfirmation, setAccountConfirmation] = useState("");

  const profileQuery = useQuery({
    queryKey: ["admin-affiliate", affiliateId],
    queryFn: () => adminApi.getAffiliate(affiliateId!),
    enabled: !!affiliateId,
  });
  const kycQuery = useQuery({
    queryKey: ["admin-affiliate-kyc", affiliateId],
    queryFn: () => adminApi.getAffiliateKyc(affiliateId!),
    enabled: !!affiliateId,
  });
  const timelineQuery = useQuery({
    queryKey: ["user-profile-timeline", profileQuery.data?.user.id],
    queryFn: () => profilesApi.getUserTimeline(profileQuery.data!.user.id, { page_size: 100 }),
    enabled: !!profileQuery.data?.user.id,
  });

  const profile = profileQuery.data;
  const kyc = kycQuery.data;
  const filledSlots = kyc?.files.map((f) => f.slot) ?? [];

  const kycMutation = useMutation({
    mutationFn: (payload: { kyc_status: "verified" | "rejected"; notes?: string }) =>
      adminApi.setAffiliateKyc(affiliateId!, payload),
    onSuccess: (_, vars) => {
      notifications.show({
        color: vars.kyc_status === "verified" ? "green" : "red",
        title: vars.kyc_status === "verified" ? "Affiliate approved" : "Affiliate rejected",
        message: vars.kyc_status === "verified" ? "KYC access has been approved." : "KYC was returned for correction.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-affiliate", affiliateId] });
      queryClient.invalidateQueries({ queryKey: ["admin-affiliate-kyc", affiliateId] });
      queryClient.invalidateQueries({ queryKey: ["admin-people"] });
      setRejectOpen(false);
      setNotes("");
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Update failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const accountMutation = useMutation({
    mutationFn: ({ action, userId, reason }: { action: "deactivate" | "reactivate"; userId: string; reason: string }) =>
      action === "deactivate"
        ? adminApi.deactivateUser(userId, reason)
        : adminApi.reactivateUser(userId, reason),
    onSuccess: (_, variables) => {
      notifications.show({
        color: "green",
        message: `Affiliate account ${variables.action === "deactivate" ? "deactivated" : "reactivated"}.`,
      });
      setAccountAction(null);
      setAccountReason("");
      setAccountConfirmation("");
      void queryClient.invalidateQueries({ queryKey: ["admin-affiliate", affiliateId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-affiliates"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-people"] });
      void queryClient.invalidateQueries({ queryKey: ["user-profile-timeline", profile?.user.id] });
    },
    onError: (err) => notifications.show({
      color: "red",
      message: err instanceof ApiError ? err.message : AFFILIATE_PROFILE_COPY.accountUpdateFailed,
    }),
  });

  if (profileQuery.isLoading || kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-280)" />;
  }
  if (!profile) {
    return <Text c="dimmed">Affiliate not found.</Text>;
  }
  const isDeactivated = profile.user.status === "suspended";
  const closeAccountModal = () => {
    setAccountAction(null);
    setAccountReason("");
    setAccountConfirmation("");
  };

  return (
    <Stack gap="var(--axio-unit-32)">
      <Button component={Link} to="/admin/affiliates" variant="subtle" w="fit-content">
        ← {AFFILIATE_PROFILE_COPY.back}
      </Button>
      <ProfileShell
        name={profile.user.full_name}
        subtitle={profile.user.email}
        role={AFFILIATE_PROFILE_COPY.role}
        status={profile.kyc_status}
        actions={
          <>
            {profile.kyc_status !== "verified" && (
              <>
                <Button
                  color="green"
                  onClick={() => kycMutation.mutate({ kyc_status: "verified" })}
                  loading={kycMutation.isPending}
                >
                  Approve
                </Button>
                <Button color="red" variant="light" onClick={() => setRejectOpen(true)}>
                  {ACCESS_GRANT_COPY.rejectCta}
                </Button>
              </>
            )}
            <Button
              color={isDeactivated ? "teal" : "red"}
              variant={isDeactivated ? "light" : "filled"}
              onClick={() => setAccountAction(isDeactivated ? "reactivate" : "deactivate")}
            >
              {isDeactivated ? AFFILIATE_PROFILE_COPY.reactivateCta : AFFILIATE_PROFILE_COPY.deactivateCta}
            </Button>
          </>
        }
        stats={
          <ProfileKeyFacts
            facts={[
              { label: AFFILIATE_PROFILE_COPY.city, value: profile.city ?? "—" },
              { label: AFFILIATE_PROFILE_COPY.registered, value: formatDate(profile.created_at) },
              { label: AFFILIATE_PROFILE_COPY.kycSubmitted, value: kyc?.kyc_submitted_at ? formatDate(kyc.kyc_submitted_at) : "—" },
              { label: AFFILIATE_PROFILE_COPY.segments, value: String(profile.segments.length) },
            ]}
          />
        }
        timeline={
          timelineQuery.isLoading ? (
            <LoadingOverlay height="var(--axio-unit-220)" />
          ) : (
            <ProfileTimeline entries={timelineQuery.data?.items ?? []} />
          )
        }
        details={
          <Stack gap="var(--axio-unit-32)">
            <Alert color="gray" title={kycStatusLabel(profile.kyc_status)}>
              {profile.kyc_notes ? `Notes: ${profile.kyc_notes}` : AFFILIATE_PROFILE_COPY.privateKyc}
            </Alert>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">{AFFILIATE_PROFILE_COPY.identityAccess}</Title>
                <Stack gap="var(--axio-unit-6)">
                  <Text size="sm">Email: {profile.user.email}</Text>
                  <Text size="sm">Phone: {profile.user.phone ?? "—"}</Text>
                  <Text size="sm">Auth provider: {profile.user.auth_provider}</Text>
                </Stack>
              </Paper>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">{AFFILIATE_PROFILE_COPY.affiliateDetails}</Title>
                <Stack gap="var(--axio-unit-6)">
                  <Text size="sm">City: {profile.city ?? "—"}</Text>
                  <Text size="sm">Aadhaar: {kyc?.aadhaar_masked ?? "—"}</Text>
                  <Text size="sm">PAN: {kyc?.pan ?? "—"}</Text>
                  <Group gap="xs" wrap="wrap">
                    {profile.segments.map((segment) => (
                      <Badge key={segment} color="violet" variant="light">
                        {SEGMENT_LABELS[segment] ?? segment}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            </SimpleGrid>
          </Stack>
        }
        documents={
          <Stack gap="md">
            <div>
              <Title order={4}>{AFFILIATE_PROFILE_COPY.kycDocuments}</Title>
              <Text size="sm" c="dimmed">{AFFILIATE_PROFILE_COPY.kycDocumentsHint}</Text>
            </div>
            {filledSlots.length === 0 ? (
              <Paper withBorder p="lg">
                <Text c="dimmed" size="sm">{AFFILIATE_PROFILE_COPY.noDocuments}</Text>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {filledSlots.map((slot) => (
                  <Paper key={slot} withBorder p="md">
                    <Stack gap="sm">
                      <Text fw="var(--axio-font-weight-semibold)" size="sm">{IDENTITY_KYC_SLOT_LABELS[slot] ?? slot}</Text>
                      <KycSlotImage affiliateId={profile.id} slot={slot} />
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            )}
          </Stack>
        }
      />

      <ConfirmDialog
        opened={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={ACCESS_GRANT_COPY.rejectAffiliateTitle}
        description={ACCESS_GRANT_COPY.rejectAffiliateBody}
        confirmLabel={ACCESS_GRANT_COPY.rejectKycCta}
        loading={kycMutation.isPending}
        confirmDisabled={!notes.trim()}
        onConfirm={() => kycMutation.mutate({ kyc_status: "rejected", notes: notes.trim() })}
      >
        <Textarea
          label={ACCESS_GRANT_COPY.reasonLabel}
          required
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </ConfirmDialog>
      <Modal
        opened={accountAction !== null}
        onClose={closeAccountModal}
        title={accountAction === "deactivate" ? AFFILIATE_PROFILE_COPY.deactivateTitle : AFFILIATE_PROFILE_COPY.reactivateTitle}
        centered
      >
        <Stack>
          <Text size="sm">
            {accountAction === "deactivate" ? AFFILIATE_PROFILE_COPY.deactivateBody : AFFILIATE_PROFILE_COPY.reactivateBody}
          </Text>
          <Textarea
            label={AFFILIATE_PROFILE_COPY.reasonLabel}
            required
            minRows={3}
            value={accountReason}
            onChange={(event) => setAccountReason(event.currentTarget.value)}
          />
          <TextInput
            label={`${AFFILIATE_PROFILE_COPY.confirmPrefix} ${profile.user.email} ${AFFILIATE_PROFILE_COPY.confirmSuffix}`}
            value={accountConfirmation}
            onChange={(event) => setAccountConfirmation(event.currentTarget.value)}
          />
          <Button
            color={accountAction === "deactivate" ? "red" : "teal"}
            loading={accountMutation.isPending}
            disabled={
              accountReason.trim().length < 3 ||
              accountConfirmation.trim().toLowerCase() !== profile.user.email.toLowerCase()
            }
            onClick={() => accountAction && accountMutation.mutate({
              action: accountAction,
              userId: profile.user.id,
              reason: accountReason.trim(),
            })}
          >
            {accountAction === "deactivate" ? AFFILIATE_PROFILE_COPY.deactivateCta : AFFILIATE_PROFILE_COPY.reactivateCta}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
